import React, { useState, useContext, useEffect, useRef } from "react";
import { AppContext } from "../context/AppContext";
import {
  Button,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Radio,
  Statistic,
  message,
  Divider,
  Input,
  Result,
} from "antd";
import {
  CheckCircleOutlined,
  FieldTimeOutlined,
  SafetyCertificateOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import "./Billing.css";
import { useParams, useNavigate } from "react-router";
import axios from "axios";

const { Title, Text } = Typography;

const PAYMENTS_API_URL =
  process.env.REACT_APP_PAYMENTS_API_URL || "http://localhost:3002/api/payments";
const POLL_INTERVAL_MS = 4000;
const MAX_POLL_ATTEMPTS = 45; // ~3 minutes

const PlanCheckout = ({ match }) => {
  const token = localStorage.getItem("token");
  const { company, setCompany } = useContext(AppContext);
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [phone, setPhone] = useState("");
  // idle -> awaiting_approval -> success | failed | timed_out
  const [paymentStage, setPaymentStage] = useState("idle");
  const pollTimerRef = useRef(null);
  const navigate = useNavigate();

  const { tier } = useParams();

  useEffect(() => {
    return () => clearInterval(pollTimerRef.current);
  }, []);

  const pollPaymentStatus = (depositId, attempt = 0) => {
    clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(async () => {
      attempt += 1;
      try {
        const { data } = await axios.get(`${PAYMENTS_API_URL}/status/${depositId}`);
        if (data.status === "COMPLETED") {
          clearInterval(pollTimerRef.current);
          setPaymentStage("success");
        } else if (data.status === "FAILED") {
          clearInterval(pollTimerRef.current);
          setPaymentStage("failed");
        } else if (attempt >= MAX_POLL_ATTEMPTS) {
          clearInterval(pollTimerRef.current);
          setPaymentStage("timed_out");
        }
      } catch (error) {
        console.error("Payment status check error:", error);
        if (attempt >= MAX_POLL_ATTEMPTS) {
          clearInterval(pollTimerRef.current);
          setPaymentStage("timed_out");
        }
      }
    }, POLL_INTERVAL_MS);
  };

  const makePayment = async () => {
    if (!phone.trim()) {
      message.error("Enter the mobile money number to pay from.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${PAYMENTS_API_URL}/initiate`, {
        companyId: company.id,
        planId: selectedPlan.id,
        phone: phone.trim(),
      });

      if (data.success) {
        setPaymentStage("awaiting_approval");
        pollPaymentStatus(data.depositId);
      } else {
        message.error(data.error || "Failed to initiate payment. Please try again.");
      }
    } catch (error) {
      console.error("Payment error:", error);
      message.error(
        error.response?.data?.error || "Failed to initiate payment. Please try again."
      );
    }
    setLoading(false);
  };

  const calculatePrice = (selectedPlan) => {
    // Get base monthly price from Directus monthly plan
    const basePlan = plans.find((p) => p.months === 1);
    const monthlyPrice = basePlan ? basePlan?.price : selectedPlan?.price;

    // Calculate based on selected duration
    const subtotal = monthlyPrice * selectedPlan?.months;
    const discount = (subtotal * selectedPlan?.discount) / 100;

    return {
      subtotal,
      discount,
      total: subtotal - discount,
    };
  };

  // Usage in component
  const currentPricing = calculatePrice(selectedPlan);

  useEffect(() => {
    if (!["Basic", "Business", "Premium"].includes(tier)) {
      navigate("/billing");
    }
  }, [tier, navigate]);

  // Fetch plans from Directus
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/plans?filter[type][_eq]=${tier.toLowerCase()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const sortedPlans = response.data.data.sort(
          (a, b) => a.months - b.months
        );
        setPlans(sortedPlans);
        setSelectedPlan(sortedPlans[0]);
      } catch (error) {
        console.error("Error fetching plans:", error);
        navigate("/billing");
      }
    };

    fetchPlans();
  }, [tier, navigate]);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setSelectedDuration(plan.months);
  };

  const calculateRenewalDate = (months) => {
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    return date;
  };

  if (!selectedPlan) return <div>Loading plans...</div>;

  return (
    <div className="checkout-container">
      <Row gutter={[24, 24]} justify="center">
        <Col xs={24} xl={18}>
          <Card className="checkout-card" hoverable>
            {/* Header Section */}
            <div className="plan-header">
              <Title level={2} style={{ marginBottom: 4 }}>
                {selectedPlan?.type?.toUpperCase()}
              </Title>
              <Text type="secondary" className="plan-subtitle">
                Flexible pricing based on commitment duration
              </Text>
            </div>

            <Divider style={{ margin: "24px 0" }} />

            {/* Main Content */}
            <Row gutter={[32, 32]} align="stretch">
              {/* Billing Cycle Column */}
              <Col xs={24} md={14}>
                <div className="section-container">
                  <Title level={4} className="section-title">
                    Choose Billing Cycle
                  </Title>

                  <div className="duration-options">
                    {plans.map((plan) => (
                      <div
                        key={plan.id}
                        className={`duration-card ${
                          selectedPlan.id === plan.id ? "active" : ""
                        }`}
                        onClick={() => handlePlanSelect(plan)}
                      >
                        <div className="duration-content">
                          <div className="duration-info">
                            <Text strong className="duration-label">
                              {plan.cycle.length > 1
                                ? plan.cycle.charAt(0).toUpperCase() +
                                  plan.cycle.slice(1)
                                : plan.cycle.charAt(0).toUpperCase()}
                            </Text>
                            {"     "}
                            {plan.discount > 0 && (
                              <Tag color="green" className="discount-tag">
                                Save {plan.discount}%
                              </Tag>
                            )}
                          </div>
                          <div className="duration-price">
                            <Text strong className="price-amount">
                              K
                              {plan.price.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Text>
                            {"     "}
                            <Text type="secondary" className="price-duration">
                              {plan.months} month
                              {plan.months > 1 ? "s" : ""}
                            </Text>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>

              {/* Order Summary Column */}
              <Col xs={24} md={10}>
                <div className="summary-container">
                  <Title level={4} className="section-title">
                    Order Summary
                  </Title>

                  <div className="price-breakdown">
                    <div className="price-row">
                      <Text className="price-label">Base Price:</Text>
                      <Text className="price-value">
                        K
                        {(
                          currentPricing.subtotal / selectedPlan?.months
                        ).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                        /month
                      </Text>
                    </div>

                    <div className="price-row">
                      <Text className="price-label">Duration:</Text>
                      <Text className="price-value">
                        {selectedPlan?.months} month
                        {selectedPlan?.months > 1 ? "s" : ""}
                      </Text>
                    </div>

                    <div className="price-row">
                      <Text className="price-label">Subtotal:</Text>
                      <Text className="price-value">
                        K
                        {selectedPlan?.price.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Text>
                    </div>

                    {selectedPlan?.discount > 0 && (
                      <div className="price-row discount">
                        <Text className="price-label">Discount:</Text>
                        <Text className="price-value">
                          - K
                          {(
                            selectedPlan?.price *
                            (selectedPlan?.discount / 100)
                          ).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Text>
                      </div>
                    )}

                    <Divider style={{ margin: "16px 0" }} />

                    <div className="total-row">
                      <Text strong className="total-label">
                        Total Due Today:
                      </Text>
                      <Title level={3} className="total-amount">
                        K
                        {(selectedPlan?.price - (selectedPlan?.price * (selectedPlan?.discount / 100))).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Title>
                    </div>
                  </div>

                  {paymentStage === "idle" && (
                    <>
                      <Input
                        size="large"
                        prefix={<MobileOutlined />}
                        placeholder="Mobile money number, e.g. 0977123456"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        style={{ marginBottom: 12 }}
                      />
                      <Button
                        type="primary"
                        block
                        size="large"
                        loading={loading}
                        className="checkout-button"
                        onClick={makePayment}
                      >
                        <SafetyCertificateOutlined />
                        Pay with Mobile Money
                      </Button>

                      <div className="renewal-notice">
                        <FieldTimeOutlined className="renewal-icon" />
                        <Text className="renewal-text">
                          Next payment:{" "}
                          {new Date(
                            Date.now() +
                              selectedPlan?.months * 30 * 24 * 60 * 60 * 1000
                          ).toLocaleDateString()}
                        </Text>
                      </div>
                    </>
                  )}

                  {paymentStage === "awaiting_approval" && (
                    <Result
                      status="info"
                      icon={<MobileOutlined />}
                      title="Check your phone"
                      subTitle={`Approve the mobile money prompt sent to ${phone} to complete this payment.`}
                    />
                  )}

                  {paymentStage === "success" && (
                    <Result
                      status="success"
                      title="Payment successful"
                      subTitle="Your plan has been activated. A receipt has been emailed to your company."
                      extra={
                        <Button type="primary" onClick={() => navigate("/")}>
                          Back to Dashboard
                        </Button>
                      }
                    />
                  )}

                  {paymentStage === "failed" && (
                    <Result
                      status="error"
                      title="Payment failed"
                      subTitle="The mobile money payment was declined or failed. You can try again."
                      extra={
                        <Button type="primary" onClick={() => setPaymentStage("idle")}>
                          Try Again
                        </Button>
                      }
                    />
                  )}

                  {paymentStage === "timed_out" && (
                    <Result
                      status="warning"
                      title="Still waiting for confirmation"
                      subTitle="This is taking longer than expected. If you approved the prompt, your plan will activate shortly — otherwise, try again."
                      extra={
                        <Button type="primary" onClick={() => setPaymentStage("idle")}>
                          Try Again
                        </Button>
                      }
                    />
                  )}
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: "24px 0" }} />

            {/* Included Features */}
            <div className="features-container">
              <CheckCircleOutlined className="feature-icon" />
              <div className="features-content">
                <Text strong className="features-title">
                  Included in all plans:
                </Text>
                <div className="features-list">
                  <span>24/7 Support</span>
                  <span>•</span>
                  <span>PCI Compliance</span>
                  <span>•</span>
                  <span>Bank-Grade Security</span>
                  {selectedPlan?.months > 1 && (
                    <>
                      <span>•</span>
                      <span>Cancel Anytime</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PlanCheckout;
